import { useDisclosure } from '@chakra-ui/hooks';
import { useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import { HttpError } from '@/lib/exception';
import {
  DETAIL_QUERY_KEY,
  DetailMode,
  ERROR_TOAST_DURATION_MS,
  formatDateTimeWithSeconds,
  isActionableStatus,
  LIST_QUERY_KEY,
  orderStatusLabel,
} from '@/pages/externals/mos-pos-resend/service';
import { routes } from '@/router';
import {
  cancelMosPosOrder,
  getMosPosOrderDetail,
  resendMosPosOrder,
} from '@/services/MosPosOrder';
import {
  MosPosOrderConflictResponse,
  MosPosOrderDetailResponse,
  MosPosOrderStatus,
} from '@/services/MosPosOrder/type';

const resendPath = (orderId: string) =>
  routes.MosPosResendConfirm.path.replace(':orderId', orderId);
const cancelPath = (orderId: string) =>
  routes.MosPosCancelConfirm.path.replace(':orderId', orderId);

const conflictToastTitle = (
  currentStatus?: MosPosOrderStatus,
  statusUpdatedAt?: string
): string => {
  // BE がラベル未定義のステータスを返しても「現在のステータス: undefined」と
  // 出さない。orderStatusLabel は Record<MosPosOrderStatus, string> なので型の上では
  // 引き当たるが、BE がステータスを追加して FE が追随していない間は実際に undefined になる。
  const label =
    currentStatus === undefined
      ? '不明'
      : orderStatusLabel[currentStatus] ?? '不明';
  const at =
    statusUpdatedAt === undefined
      ? ''
      : `、更新日時: ${formatDateTimeWithSeconds(statusUpdatedAt)}`;
  return `この注文はすでに処理済みです（現在のステータス: ${label}${at}）。`;
};

/**
 * SCR002 (再送信確認) / SCR003 (取消確認) で共通のデータ取得・判定・実行ロジック
 *
 * @param enabled 詳細を取得してよいかどうかです。呼び出し側のアクセス判定を渡します。
 *   アカウントの読み込みが完了するまでは false になるため、判定前のリクエストが
 *   発生しません。
 */
export const useMosPosOrderDetailPage = (mode: DetailMode, enabled = true) => {
  const history = useHistory();
  const { orderId } = useParams<{ orderId: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const conflictHandledRef = useRef(false);
  const isMountedRef = useRef(true);

  const { data, isLoading, error } = useQuery(
    [DETAIL_QUERY_KEY, orderId],
    () => getMosPosOrderDetail(orderId),
    { enabled }
  );

  // setTimeout での遷移は必ず後片付けする
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (redirectTimerRef.current !== null) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  // 一覧への引き戻しは多重に予約しない。
  // 実行直前の再取得はキャッシュを更新するため、handleExecute と [data] の effect が
  // 同じ競合を続けて検知しうる。上書きすると先に張ったタイマーが解除されずに残り、
  // アンマウント後に history.replace が走る。
  const redirectToListAfterDelay = useCallback(() => {
    if (redirectTimerRef.current !== null) return;
    setIsRedirecting(true);
    redirectTimerRef.current = setTimeout(() => {
      history.replace(routes.MosPosResendList.path);
    }, ERROR_TOAST_DURATION_MS);
  }, [history]);

  const showConflictToast = useCallback(
    // statusUpdatedAt は null もありうる。BE は競合検知後に最新ステータスを読み直すが、
    // その読み直しに失敗した場合だけ status_updated_at が null で返る。
    (currentStatus?: MosPosOrderStatus, statusUpdatedAt?: string | null) => {
      toast({
        title: conflictToastTitle(currentStatus, statusUpdatedAt ?? undefined),
        status: 'error',
        duration: ERROR_TOAST_DURATION_MS,
        isClosable: true,
      });
    },
    [toast]
  );

  // ステータス競合の後始末（トースト + 一覧への引き戻し）は 1 回だけ行う。
  // 同じ競合を [data] の effect と handleExecute の両方が検知しうるため、
  // ここで束ねないと同じ文言のトーストが 2 枚出る。
  const handleConflict = useCallback(
    (currentStatus?: MosPosOrderStatus, statusUpdatedAt?: string | null) => {
      if (conflictHandledRef.current) return;
      conflictHandledRef.current = true;
      showConflictToast(currentStatus, statusUpdatedAt);
      redirectToListAfterDelay();
    },
    [showConflictToast, redirectToListAfterDelay]
  );

  // 詳細取得に失敗したときのフォールバック。
  // この画面は詳細が取れないと表示も操作もできないため、トーストを出して一覧へ戻す。
  // (放置すると data が undefined のままで「注文情報を読み込み中…」が消えない)
  useEffect(() => {
    if (error === null || error === undefined) return;

    toast({
      title: '注文情報の取得に失敗しました。',
      status: 'error',
      duration: ERROR_TOAST_DURATION_MS,
      isClosable: true,
    });
    redirectToListAfterDelay();
    // NOTE エラーが変化したときだけトーストを出す
  }, [error]);

  // 画面表示時のチェック。① API が判定した操作可否 → ②ステータス競合 の順序は固定。
  // ブラウザ時計で48時間を再計算するとサーバー側guardとずれるため、画面の選択は API の
  // can_resend / can_cancel を唯一の根拠にする。
  useEffect(() => {
    if (data === undefined) return;

    // ① 操作可否判定
    if (mode === 'resend' && !data.can_resend && data.can_cancel) {
      history.replace(cancelPath(orderId));
      return;
    }
    if (mode === 'cancel' && !data.can_cancel && data.can_resend) {
      history.replace(resendPath(orderId));
      return;
    }

    // ② ステータス競合判定
    if (!isActionableStatus(data.status)) {
      handleConflict(data.status, data.status_updated_at);
    }
    // NOTE 判定は詳細取得のたびに1度だけ行えばよいので依存は data のみ
  }, [data]);

  const handleExecute = useCallback(async () => {
    if (data === undefined) return;

    setIsSubmitting(true);
    try {
      // 画面を開いたまま操作可能期間を跨ぐ可能性があるため、実行直前に API の
      // server clock で再判定された capability と最新ステータスを取得する。
      //
      // 成功時だけキャッシュへ書き戻し、遷移先の画面が古い capability を見ないようにする。
      let latestDetail: MosPosOrderDetailResponse;
      try {
        latestDetail = await getMosPosOrderDetail(orderId);
      } catch {
        toast({
          title:
            '注文情報の再取得に失敗しました。時間をおいて再度お試しください。',
          status: 'error',
          duration: ERROR_TOAST_DURATION_MS,
          isClosable: true,
        });
        onClose();
        return;
      }
      queryClient.setQueryData([DETAIL_QUERY_KEY, orderId], latestDetail);

      // ① 操作可否判定
      const canExecute =
        mode === 'resend' ? latestDetail.can_resend : latestDetail.can_cancel;
      if (!canExecute) {
        onClose();
        if (latestDetail.can_resend) {
          history.replace(resendPath(orderId));
          return;
        }
        if (latestDetail.can_cancel) {
          history.replace(cancelPath(orderId));
          return;
        }
        handleConflict(latestDetail.status, latestDetail.status_updated_at);
        return;
      }

      // ② ステータス競合判定。can_resend / can_cancel は注文日時からの 48 時間だけを
      //    見ておりステータスを含まない (BE: ResendOrder.CanResend) ため、他セッションが
      //    先に処理した注文はここで弾かないと必ず 409 になる POST を投げてしまう。
      if (!isActionableStatus(latestDetail.status)) {
        onClose();
        handleConflict(latestDetail.status, latestDetail.status_updated_at);
        return;
      }

      const actionResult =
        mode === 'resend'
          ? await resendMosPosOrder(orderId)
          : await cancelMosPosOrder(orderId);
      await queryClient.invalidateQueries([LIST_QUERY_KEY]);
      onClose();
      history.push(routes.MosPosResendList.path, {
        toast: { type: mode, webOrderId: actionResult.web_order_id },
      });
    } catch (error) {
      if (error instanceof HttpError && error.status === 409) {
        // 他セッション / FDS 側で処理済み。一覧へ戻す。
        const conflict = error.data as MosPosOrderConflictResponse | undefined;
        onClose();
        handleConflict(conflict?.current_status, conflict?.status_updated_at);
        return;
      }

      // 409 以外は画面に留まる。
      const fallbackMessage =
        mode === 'resend'
          ? '再送信に失敗しました。時間をおいて再度お試しください。'
          : '取消に失敗しました。時間をおいて再度お試しください。';
      toast({
        title: fallbackMessage,
        status: 'error',
        duration: ERROR_TOAST_DURATION_MS,
        isClosable: true,
      });
      onClose();
    } finally {
      // 成功時は history.push でこの画面が外れているため、アンマウント後の
      // state 更新にならないようにする。
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [
    data,
    handleConflict,
    history,
    mode,
    onClose,
    orderId,
    queryClient,
    toast,
  ]);

  const handleBack = useCallback(() => {
    history.push(routes.MosPosResendList.path);
  }, [history]);

  return {
    detail: data,
    // enabled が false の間 react-query は idle になり isLoading が false になります。
    // そのまま返すと未取得の内容を描画してしまうため、読み込み中として扱います。
    isLoading: !enabled || isLoading,
    /** 詳細取得に失敗した状態。ローディング表示を続けないために画面側で分岐する */
    hasError: error !== null && error !== undefined,
    /** 遷移待ちの間は操作ボタンを出さない */
    isRedirecting,
    isSubmitting,
    canExecute:
      data !== undefined &&
      (mode === 'resend' ? data.can_resend : data.can_cancel),
    modal: { isOpen, onOpen, onClose },
    handlers: { handleExecute, handleBack },
  };
};
